/**
 * "Download for processing" on the Captures tab (CapturesGallery.jsx): an
 * admin, signed in as themselves, downloads one capture's photos to a studio
 * computer to process them there (docs/ingest/14-capture-upload-setup.md,
 * "Download for processing"). Shown only to admins, and only on captures whose
 * upload tag says In the cloud.
 *
 * The first time in a browser it asks which computer this is (P6, E6,
 * Capture PC, or another short name) and remembers it in this browser only
 * (localStorage; a browser that refuses storage simply asks again). The
 * confirm says whose photos these are and where they go, with Change. OK asks
 * the Suite for two links (getCaptureDownloadLinks: good for 10 minutes, each
 * download recorded with who asked and the computer named) and starts the two
 * downloads, the photos first, then the info file. The computer is what the
 * person says, not a checked machine identity.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";

export const DOWNLOAD_CONFIRM_TEXT = "These are a guest's photos. Delete the downloaded copy once it's processed.";
export const COMPUTER_KEY = "suite.captureDownload.computer";
export const COMPUTER_CHOICES = ["P6", "E6", "Capture PC"];
export const COMPUTER_MAX = 40;
// The same rule as the callable (functions/src/services/captureDownload.ts).
const COMPUTER_RE = /^[A-Za-z0-9][A-Za-z0-9 ._'#()-]*$/;
const COMPUTER_HINT = "Use letters and numbers (spaces and . _ ' # ( ) - too), at most 40 characters.";

/** Who sees the button: Suite admins (the callable itself takes admins only). */
export const canDownloadForProcessing = (user) => user?.role === "admin" || user?.role === "superadmin";

/** The computer name as typed, tidied, or null when it does not fit the rule. */
export function cleanComputerLabel(value) {
  const s = typeof value === "string" ? value.trim().replace(/\s+/g, " ") : "";
  return s.length >= 1 && s.length <= COMPUTER_MAX && COMPUTER_RE.test(s) ? s : null;
}

const browserStorage = () => {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
};

/** The computer this browser was named, or null (never named, or storage refused). */
export function readComputer(storage = browserStorage()) {
  try {
    return cleanComputerLabel(storage?.getItem(COMPUTER_KEY) ?? "");
  } catch {
    return null;
  }
}

/** Remember the computer in this browser. False when the browser refuses (it is asked again next time). */
export function rememberComputer(label, storage = browserStorage()) {
  try {
    if (!storage) return false;
    storage.setItem(COMPUTER_KEY, label);
    return true;
  } catch {
    return false;
  }
}

/** 1070000000 -> "1.07 GB" (decimal units, as the computer's disk shows them). */
export function formatSize(bytes) {
  if (typeof bytes !== "number" || !Number.isFinite(bytes) || bytes < 0) return "size unknown";
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(2)} GB`;
  if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(1)} MB`;
  if (bytes >= 1e3) return `${Math.round(bytes / 1e3)} KB`;
  return `${bytes} bytes`;
}

const STORAGE_LINK = /^https:\/\/storage\.googleapis\.com\/[^\s"'<>]+$/;

/** True when the Suite's answer holds two Cloud Storage links. */
export function hasDownloadLinks(r) {
  return STORAGE_LINK.test(String(r?.photos?.url ?? "")) && STORAGE_LINK.test(String(r?.info?.url ?? ""));
}

export const downloadStartedText = (r) => `Downloading: photos (${formatSize(r?.photos?.bytes)}) and info file`;

/**
 * Start the downloads in this tab, in order: the first at once, each next
 * one a moment later (the browser may ask once to allow several downloads).
 * The links answer with "save as" (attachment), so the page stays open. They
 * were signed a moment ago for files the Suite just found in the cloud; the
 * fallback links on the tile, which may be pressed minutes later, open in a
 * new tab instead, so an expired link never replaces the Suite.
 */
export function startDownloads(urls, { doc = globalThis.document, later = (ms, fn) => setTimeout(fn, ms), gapMs = 1500 } = {}) {
  urls.forEach((url, i) => {
    const go = () => {
      const a = doc.createElement("a");
      a.href = url;
      a.rel = "noopener noreferrer";
      a.style.display = "none";
      doc.body.appendChild(a);
      a.click();
      a.remove();
    };
    if (i === 0) go();
    else later(i * gapMs, go);
  });
}

/**
 * OK pressed: ask the Suite for the two links (recorded there with the
 * computer named), then start the photos, then the info file. Nothing starts
 * unless both links are Cloud Storage links. Resolves { result } or { error }
 * (plain words); never throws.
 */
export async function runDownload({ jobId, sn, computer, getLinks, start = startDownloads }) {
  try {
    const r = await getLinks({ jobId, sn, computer });
    if (!hasDownloadLinks(r)) throw new Error("the Suite's answer had no download links");
    start([r.photos.url, r.info.url]);
    return { result: r };
  } catch (err) {
    return { error: downloadErrorText(err) };
  }
}

const callCode = (err) => String(err?.code ?? "").replace(/^functions\//, "");

/** The Suite's refusal, in plain words. */
export function downloadErrorText(err) {
  const reason = err?.details?.reason;
  if (reason === "not_in_cloud") return "These photos are not in the cloud (not uploaded and checked yet). Wait until the tile says In the cloud, then try again.";
  if (reason === "discarded") return "This capture was discarded, so its photos cannot be downloaded.";
  if (reason === "delivery_closed") return "This guest's delivery has ended (deleted or expired), so the photos cannot be downloaded.";
  if (reason === "version_deleting") return "This client capture's photos are being deleted, so they cannot be downloaded.";
  if (reason === "other_job") return "This capture belongs to another job.";
  if (reason === "info_unusable") return "This capture's info file cannot be made without guests' details. Ask Ivin to look at it.";
  if (reason === "changed") return "The upload changed while the links were being made. Try again.";
  if (reason === "event-account") return "Only an admin can download photos for processing.";
  const code = callCode(err);
  const message = typeof err?.message === "string" ? err.message : "";
  if (code === "permission-denied") {
    return /^Requires role/.test(message) || !message ? "Only an admin can download photos for processing." : message;
  }
  if (code === "unauthenticated") return "You are signed out. Sign in again, then try again.";
  if (code === "invalid-argument") return `The Suite did not accept this. ${/computer/.test(message) ? `Check the computer name: ${COMPUTER_HINT}` : message}`;
  if (code === "deadline-exceeded" || code === "unavailable") return "No answer from the Suite. Check the internet connection and try again.";
  if (code === "not-found") return "The Suite could not find this capture's photos.";
  if (code === "internal") return "The Suite could not make the download links. Try again in a moment; if it keeps failing, tell Ivin.";
  return `The download did not start: ${message || String(err)}`;
}

/**
 * One tile's control. Pure given its props (SSR-tested):
 *   step null       the button, and after a download what was started
 *   step "pick"     Which computer is this? P6 / E6 / Capture PC / Other
 *   step "confirm"  the guest's-photos sentence, "Downloading to <computer>. Change", OK / Cancel
 * busy: this tile's links are being fetched ("Getting links for <sn>..." on
 * its OK). locked: some tile's links are being fetched, so no Download for
 * processing button or OK can start another one until it answers.
 */
export function DownloadControl({
  show, sn = "", step = null, computer = null, otherOpen = false, draft = "", draftError = null,
  busy = false, locked = false, result = null, linksExpired = false, error = null, on = {},
}) {
  if (!show) return null;
  if (step === "pick") {
    return (
      <div className="rigv2-tile-actions rigv2-dl">
        <span className="rigv2-tile-confirm">Which computer is this?</span>
        {COMPUTER_CHOICES.map((c) => (
          <button key={c} type="button" className="rigv2-tile-btn" onClick={() => on.pick?.(c)}>{c}</button>
        ))}
        <button type="button" className={`rigv2-tile-btn ${otherOpen ? "on" : ""}`} onClick={() => on.openOther?.()}>Other</button>
        {otherOpen && (
          <span className="rigv2-dl-other">
            <input
              className="rigv2-dl-input"
              aria-label="Computer name"
              placeholder="Computer name"
              maxLength={COMPUTER_MAX}
              value={draft}
              onChange={(e) => on.draft?.(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") on.useDraft?.(); }}
            />
            <button type="button" className="rigv2-tile-btn" onClick={() => on.useDraft?.()}>Use this name</button>
          </span>
        )}
        {draftError && <span className="rigv2-tile-err" role="alert">{draftError}</span>}
        <button type="button" className="rigv2-tile-btn" onClick={() => on.cancel?.()}>Cancel</button>
      </div>
    );
  }
  if (step === "confirm") {
    return (
      <div className="rigv2-tile-actions rigv2-dl">
        <span className="rigv2-tile-confirm">{DOWNLOAD_CONFIRM_TEXT}</span>
        <span className="rigv2-dl-where">
          Downloading to <strong>{computer}</strong>.{" "}
          <button type="button" className="rigv2-dl-link" disabled={busy} onClick={() => on.change?.()}>Change</button>
        </span>
        <button type="button" className="rigv2-tile-btn primary" disabled={busy || locked} onClick={() => on.confirm?.()}>{busy ? `Getting links for ${sn}…` : "OK"}</button>
        <button type="button" className="rigv2-tile-btn" disabled={busy} onClick={() => on.cancel?.()}>Cancel</button>
        {error && <span className="rigv2-tile-err" role="alert">{error}</span>}
      </div>
    );
  }
  return (
    <div className="rigv2-tile-actions rigv2-dl">
      <button type="button" className="rigv2-tile-btn" disabled={locked} onClick={() => on.ask?.()}>Download for processing</button>
      {result && (
        <span className="rigv2-dl-ok" role="status">
          {downloadStartedText(result)}.
          {!linksExpired && (
            <> If one did not start: <a href={result.photos.url} target="_blank" rel="noopener noreferrer">photos</a>, <a href={result.info.url} target="_blank" rel="noopener noreferrer">info file</a> (these links work for 10 minutes).</>
          )}
          <span className="rigv2-dl-md5">Photos MD5: <span className="mono">{result.photos.md5}</span></span>
        </span>
      )}
      {error && <span className="rigv2-tile-err" role="alert">{error}</span>}
    </div>
  );
}

// The fallback links are hidden a little before the server's 10 minutes run out.
const LINKS_SHOWN_MS = 9 * 60 * 1000;

/**
 * The state behind every tile's control (one confirm open at a time, one
 * download request at a time). propsFor(sn, tag, canDownload) gives
 * DownloadControl its props. A finished request closes only the confirm that
 * started it, and nothing is updated once the Captures tab is gone.
 */
export function useCaptureDownload({ jobId, getLinks, storage, start = startDownloads }) {
  const [computer, setComputer] = useState(() => readComputer(storage));
  const [open, setOpen] = useState(null); // { sn, step, otherOpen, draft, draftError }
  const [busySn, setBusySn] = useState(null);
  const [notes, setNotes] = useState({}); // sn -> { result, linksExpired, error }
  const timers = useRef(new Set());
  const pending = useRef(null); // the request in flight: { sn, id }, set at once (state lags a render)
  const nextId = useRef(0);
  const mounted = useRef(false);
  useEffect(() => {
    const t = timers.current;
    mounted.current = true;
    return () => { mounted.current = false; for (const id of t) clearTimeout(id); t.clear(); };
  }, []);

  const note = useCallback((sn, value) => setNotes((n) => ({ ...n, [sn]: value })), []);

  const ask = (sn) => {
    if (pending.current) return;
    note(sn, null);
    setOpen({ sn, step: computer ? "confirm" : "pick", otherOpen: false, draft: "", draftError: null });
  };
  const pick = (sn, label) => {
    const c = cleanComputerLabel(label);
    if (!c) {
      setOpen((o) => ({ ...o, sn, step: "pick", otherOpen: true, draftError: COMPUTER_HINT }));
      return;
    }
    rememberComputer(c, storage);
    setComputer(c);
    setOpen({ sn, step: "confirm", otherOpen: false, draft: "", draftError: null });
  };
  const confirm = async (sn) => {
    if (pending.current) return;
    if (!computer) {
      setOpen({ sn, step: "pick", otherOpen: false, draft: "", draftError: null });
      return;
    }
    const requestId = ++nextId.current;
    pending.current = { sn, id: requestId };
    setBusySn(sn);
    setOpen((o) => (o?.sn === sn ? { ...o, requestId } : o));
    note(sn, null);
    const { result: r, error } = await runDownload({ jobId, sn, computer, getLinks, start });
    if (!mounted.current) return;
    if (pending.current?.id === requestId) pending.current = null;
    setBusySn((b) => (b === sn ? null : b));
    if (error) {
      note(sn, { result: null, linksExpired: false, error });
      return;
    }
    note(sn, { result: r, linksExpired: false, error: null });
    setOpen((o) => (o?.requestId === requestId ? null : o));
    const id = setTimeout(() => {
      timers.current.delete(id);
      setNotes((n) => (n[sn]?.result === r ? { ...n, [sn]: { ...n[sn], linksExpired: true } } : n));
    }, LINKS_SHOWN_MS);
    timers.current.add(id);
  };

  const propsFor = (sn, tag, canDownload) => {
    const mine = open?.sn === sn ? open : null;
    const n = notes[sn] ?? null;
    return {
      show: Boolean(canDownload) && tag?.cls === "verified",
      sn,
      step: mine?.step ?? null,
      computer,
      otherOpen: mine?.otherOpen ?? false,
      draft: mine?.draft ?? "",
      draftError: mine?.draftError ?? null,
      busy: busySn === sn,
      locked: busySn !== null,
      result: n?.result ?? null,
      linksExpired: n?.linksExpired ?? false,
      error: n?.error ?? null,
      on: {
        ask: () => ask(sn),
        pick: (label) => pick(sn, label),
        openOther: () => setOpen((o) => ({ ...(o?.sn === sn ? o : { sn, step: "pick", draft: "" }), otherOpen: true, draftError: null })),
        draft: (text) => setOpen((o) => (o?.sn === sn ? { ...o, draft: text, draftError: null } : o)),
        useDraft: () => pick(sn, mine?.draft ?? ""),
        change: () => setOpen({ sn, step: "pick", otherOpen: false, draft: "", draftError: null }),
        confirm: () => confirm(sn),
        cancel: () => { setOpen(null); note(sn, null); },
      },
    };
  };
  return { propsFor, computer };
}
